import { quat } from "gl-matrix";

import { quatTop } from "./gl-matrix";

// https://www.gamedev.net/forums/topic/461253-compressed-quaternions/4041224/?page=2
// https://www.reddit.com/r/gamedev/comments/7ouzym/can_someone_explain_compressing_quaternions_to_me/
export class QuaternionPacker {
  static maxValue = 0.7072; // bit bigger than 1 / sqrt(2)
  static vQuant = [0, 0, 0];

  /**
   * Quantizes and packs quaternion according to "send lowest 3 components" approach.
   *
   * Each component is quantized into a 10 bit two's complement integer. Note
   * that rather being quantized into range +/-1, we put it in the range +/- 0.7072,
   * since the largest the 3 smallest components can be is `1 / sqrt(2)`.
   *
   * Order of packed, 3 smallest components is according to:
   *
   * ```
   * i => (largest + i + 1) % 4
   * ```
   *
   * Return type as a C struct would be:
   *
   * ```
   * typedef union
   *  {
   *    struct
   *    {
   *      uint32_t largestIndex :  2;
   *      uint32_t c            : 10;
   *      uint32_t b            : 10;
   *      uint32_t a            : 10;
   *    };
   *    uint32_t mask;
   *  } quat_packed_t;
   * ```
   *
   * @param q quaternion to pack
   * @returns uint32 (in a js number) of the form
   */
  static pack(q: quat): number | undefined {
    // find largest component
    let largestIndex = 0;
    let largest = Math.abs(q[0]);
    for (let i = 1; i < 4; i++) {
      const val = Math.abs(q[i]);
      if (val > largest) {
        largestIndex = i;
        largest = val;
      }
    }

    const v = this.vQuant;
    // if the omitted component is negative, flip the entire quaternion
    // so that omitted is always positive
    const flip = q[largestIndex] > 0 ? 1 : -1;
    for (let i = 0; i < 3; i++) {
      const val = q[(largestIndex + i + 1) % 4] * flip;
      const encoded = this.quantizeFloat(val);
      if (encoded === undefined) {
        console.error("can't encode value", q, quat.len(q));
        return undefined;
      }
      v[i] = encoded;
    }

    // pack each component into 10 bits, writing the largest (omitted index) to the trailing
    // 2 bits
    return (
      ((0x3ff & v[0]) << 22) |
      ((0x3ff & v[1]) << 12) |
      ((0x3ff & v[2]) << 2) |
      (0x3 & largestIndex)
    );
  }

  static unpack(n: number): quat | undefined {
    const q: quat = quat.create();
    // mask bottom 2 bits to get largest (omitted) index
    const largestIndex = n & 0x3;

    // unpack 10 bit components
    let sqSum = 0;
    const offsets = [22, 12, 2];
    for (let i = 0; i < 3; i++) {
      const decoded = this.dequantizeFloat((n >> offsets[i]) & 0x3ff);
      if (decoded === undefined) {
        console.error("error decoding quaternion");
        return undefined;
      }
      q[(largestIndex + i + 1) % 4] = decoded;
      sqSum += decoded ** 2;
    }

    // calculate omitted component
    q[largestIndex] = Math.sqrt(1 - sqSum);

    // ensure that w is positive (optional)
    quatTop(q);

    return q;
  }

  // https://en.wikipedia.org/wiki/Two%27s_complement
  private static quantizeFloat(v: number): number | undefined {
    if (Math.abs(v) > this.maxValue) {
      console.error("invalid quaternion component found", v);
      return undefined;
    }

    // quantize / normalize float into +ve integer up to 511
    // (we ignore maximum -ve value in two's complement)
    let value = Math.round((Math.abs(v) / this.maxValue) * 511);

    // if component is -ve, make the two's complement integer transformation
    // by flipping all bits in the number and adding 1
    if (v < 0.0) value = ((value ^ 0x3ff) + 1) & 0x3ff;
    return value;
  }

  private static dequantizeFloat(v: number): number | undefined {
    // if leading bit is set, then underlying float is -ve, so we flip that
    // leading bit, then subtract the maximum value (which is what the set leading
    // bit means in two's complement)
    if (v & 0x200) {
      v ^= 0x200;
      v -= 512;
    }

    // de-normalize back into floating point
    return (v / 511) * this.maxValue;
  }
}
